// D49 — pure occurrence engine for recurring finance rules (no side effects).
// Tách khỏi bento-derive để giữ file gọn; recurringForMonth chỉ map kết quả ở đây.
//
// Tần suất hỗ trợ: monthly (mặc định, back-compat), weekly (mỗi 7 ngày từ
// startDate), quarterly (mỗi 3 tháng kể từ tháng start), yearly (mỗi 12 tháng).
// endOfMonth: dùng ngày cuối tháng thay cho dayOfMonth (monthly/quarterly/yearly).

import { addDaysIso, todayIso } from "../lib/date";
import type { RecurringFrequency, RecurringRule } from "../types";

const pad = (n: number) => String(n).padStart(2, "0");

/** Số ngày của tháng (m1 = tháng 1-based). */
function lastDayOfMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

/** Số tháng lệch giữa hai monthKey "YYYY-MM" (b − a); âm khi b trước a. */
function monthsBetween(aMonthKey: string, bMonthKey: string): number {
  const [ay, am] = aMonthKey.split("-").map(Number);
  const [by, bm] = bMonthKey.split("-").map(Number);
  return (by - ay) * 12 + (bm - am);
}

/** Ngày phát sinh trong monthKey cho lịch tháng/quý/năm (clamp dayOfMonth vào số ngày tháng). */
function monthlyDate(monthKey: string, dayOfMonth: number, endOfMonth?: boolean): string {
  const [y, m] = monthKey.split("-").map(Number);
  const last = lastDayOfMonth(y, m);
  const day = endOfMonth ? last : Math.min(Math.max(1, dayOfMonth), last);
  return `${monthKey}-${pad(day)}`;
}

/** Các ngày weekly (mỗi 7 ngày từ startDate) rơi vào monthKey. */
function weeklyOccurrences(startIso: string, monthKey: string): string[] {
  const [y, m] = monthKey.split("-").map(Number);
  const monthStartIso = `${monthKey}-01`;
  const monthEndIso = `${monthKey}-${pad(lastDayOfMonth(y, m))}`;
  // Lần phát sinh đầu tiên >= max(startDate, đầu tháng), bám cadence 7 ngày từ start.
  const startD = new Date(`${startIso}T00:00:00`);
  let cursorIso = startIso;
  if (monthStartIso > startIso) {
    const monthStartD = new Date(`${monthStartIso}T00:00:00`);
    const diffDays = Math.round((monthStartD.getTime() - startD.getTime()) / 86_400_000);
    const steps = Math.ceil(diffDays / 7);
    cursorIso = addDaysIso(steps * 7, startD);
  }
  const out: string[] = [];
  while (cursorIso <= monthEndIso) {
    if (cursorIso >= monthStartIso) out.push(cursorIso);
    cursorIso = addDaysIso(7, new Date(`${cursorIso}T00:00:00`));
  }
  return out;
}

/** Ngày phát sinh "thô" trong monthKey (chưa lọc startDate/today). */
function rawOccurrences(rule: RecurringRule, monthKey: string): string[] {
  const freq: RecurringFrequency = rule.frequency ?? "monthly";
  if (freq === "weekly") return weeklyOccurrences(rule.startDate, monthKey);

  const diff = monthsBetween(rule.startDate.slice(0, 7), monthKey);
  if (diff < 0) return [];
  if (freq === "quarterly" && diff % 3 !== 0) return [];
  if (freq === "yearly" && diff % 12 !== 0) return [];
  return [monthlyDate(monthKey, rule.dayOfMonth, rule.endOfMonth)];
}

/**
 * Ngày phát sinh thực tế của 1 rule trong monthKey, đã lọc:
 *  - >= startDate (tránh phát sinh trước khi rule tồn tại / trước ngày bắt đầu)
 *  - tháng tương lai → rỗng; tháng hiện tại → chỉ tính ngày <= today (chưa tới thì chưa ghi)
 *  - tháng quá khứ → tính đủ
 */
export function recurringOccurrenceDates(
  rule: RecurringRule,
  monthKey: string,
  today = todayIso(),
): string[] {
  const todayMonth = today.slice(0, 7);
  if (monthKey > todayMonth) return [];
  return rawOccurrences(rule, monthKey)
    .filter((date) => date >= rule.startDate)
    .filter((date) => !rule.endDate || date <= rule.endDate) // D51 dừng sau ngày kết thúc
    .filter((date) => (monthKey < todayMonth ? true : date <= today))
    .sort();
}

const WEEKDAYS_SHORT = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];

/** "2026-12-31" → "31/12/2026" cho nhãn ngày kết thúc. */
function shortDate(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/** Nhãn lịch thường ngữ cho UI ("Ngày 5 hằng tháng", "Hằng tuần · T6"…). */
export function recurringScheduleLabel(rule: RecurringRule): string {
  const freq: RecurringFrequency = rule.frequency ?? "monthly";
  const base = (() => {
    if (freq === "weekly") {
      const wd = WEEKDAYS_SHORT[new Date(`${rule.startDate}T00:00:00`).getDay()];
      return `Hằng tuần · ${wd}`;
    }
    const dayPart = rule.endOfMonth ? "ngày cuối tháng" : `ngày ${rule.dayOfMonth}`;
    if (freq === "quarterly") return `Hằng quý · ${dayPart}`;
    if (freq === "yearly") return `Hằng năm · ${dayPart} tháng ${Number(rule.startDate.slice(5, 7))}`;
    return rule.endOfMonth ? "Ngày cuối tháng" : `Ngày ${rule.dayOfMonth} hằng tháng`;
  })();
  return rule.endDate ? `${base} · đến ${shortDate(rule.endDate)}` : base;
}
